import { apiClient } from "../core/apiClient";
import CashSessionService, { ICashMovementData } from "./cashSessionService";

export interface IPOSProduct {
  id: string;
  category_id: number;
  nombre: string;
  descripcion: string;
  precio: string;
  imagen_url?: string;
  esta_disponible: boolean;
  type: string;
  pack_size?: number;
}

export interface IPOS {
  id: number;
  nombre: string;
  descripcion: string;
  activo: boolean;
}

export interface IPOSListResponse {
  success: boolean;
  message: string;
  data: IPOS[];
  meta: {
    total: number;
    soloActivos: boolean;
    timestamp: string;
  };
}

export interface IPOSProductsResponse {
  success: boolean;
  message: string;
  data: IPOSProduct[];
  meta: {
    posId: number;
    totalProductos: number;
    timestamp: string;
  };
}

export interface ISaleData {
  items: Array<{
    productId: string;
    quantity: number;
    price: number;
  }>;
  total: number;
  paymentMethod: "cash" | "card";
  posId: number; // ID del POS donde se registra la venta
  cliente?: any; // Cliente information
  metodoPago?:
    | "efectivo"
    | "tarjeta_debito"
    | "tarjeta_credito"
    | "credito_interno"
    | "subsidio"; // Payment method
  fecha?: string; // Sale date
}

// Interfaz para consulta de movimientos
export interface ICashMovement {
  id: string;
  tipo: string;
  monto: number;
  descripcion: string;
  creadoEn: string;
}

// Interfaz para resumen de movimientos
export interface ICashMovementSummary {
  totalIngresos: number;
  totalRetiros: number;
  totalAjustes: number;
  cantidadMovimientos: number;
  efectivoCalculado: number;
  sesionId: string;
}

// Interfaz para el backend
export interface IBackendSaleData {
  cliente: {
    nombre: string;
    email?: string;
    id?: string;
  };
  items: Array<{
    productoId: string;
    cantidad: number;
    precioUnitario: number;
  }>;
  posId: number; // ID del POS donde se registra la venta
}

export interface IPaymentData {
  amount: number;
  paymentMethod: "cash" | "card";
  saleId: string;
  cajeroId: string;
}

class POSService {
  /**
   * Obtiene la lista de POS disponibles
   */
  static async getPOSList(): Promise<IPOS[]> {
    try {
      const response = await apiClient.get<IPOSListResponse>("/api/pos/lista");
      return response.data.data;
    } catch (error) {
      console.error("Error fetching POS list:", error);
      throw new Error("Failed to fetch POS list");
    }
  }

  /**
   * Obtiene la lista de POS disponibles (pública, sin auth)
   */
  static async getPublicPOSList(): Promise<IPOS[]> {
    try {
      const response = await apiClient.get<IPOSListResponse>("/api/pos/lista-publica");
      return response.data.data;
    } catch (error) {
      console.error("Error fetching public POS list:", error);
      throw new Error("Failed to fetch public POS list");
    }
  }

  /**
   * Obtiene productos específicos para un POS
   */
  static async getProducts(posId: number): Promise<IPOSProduct[]> {
    try {
      const response = await apiClient.get<IPOSProductsResponse>(`/api/pos/productos/${posId}`);
      
      // Verificar que la respuesta tenga la estructura esperada
      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        return response.data.data.map((product: any) => ({
          id: product.id.toString(),
          category_id: product.category_id,
          nombre: product.nombre,
          descripcion: product.descripcion,
          precio: product.precio.toString(),
          imagen_url: product.imagen_url,
          esta_disponible: product.esta_disponible,
          type: product.type,
          pack_size: product.pack_size,
        }));
      }
      
      return [];
    } catch (error) {
      console.error("Error fetching products for POS:", error);
      throw new Error(`Failed to fetch products for POS ${posId}`);
    }
  }

  /**
   * Obtiene productos por POS (público, sin auth)
   */
  static async getPublicProducts(posId: number): Promise<IPOSProduct[]> {
    try {
      const response = await apiClient.get<IPOSProductsResponse>(`/api/pos/productos-publico/${posId}`);
      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        return response.data.data.map((product: any) => ({
          id: product.id.toString(),
          category_id: product.category_id,
          nombre: product.nombre,
          descripcion: product.descripcion,
          precio: product.precio.toString(),
          imagen_url: product.imagen_url,
          esta_disponible: product.esta_disponible,
          type: product.type,
          pack_size: product.pack_size,
        }));
      }
      return [];
    } catch (error) {
      console.error("Error fetching public products for POS:", error);
      throw new Error(`Failed to fetch public products for POS ${posId}`);
    }
  }

  /**
   * Obtiene productos generales (sin POS específico) - DEPRECATED
   * @deprecated Use getProducts(posId) instead
   */
  static async getAllProducts(): Promise<IPOSProduct[]> {
    const response = await apiClient.get("/api/pos/productos");
    return response.data;
  }

  /**
   * Registra una nueva venta
   */
  static async registerSale(saleData: ISaleData): Promise<any> {
    const response = await apiClient.post("/api/pos/ventas", saleData);
    return response.data;
  }

  /**
   * Procesa una venta completa (registra y paga)
   */
  static async processSale(saleData: ISaleData): Promise<any> {
    // Mapear datos al formato que espera el backend
    const backendData: IBackendSaleData = {
      cliente: {
        nombre:
          saleData.cliente?.nombre ||
          (saleData.cliente?.tipo === "general"
            ? "Público General"
            : "Cliente Registrado"),
        email: saleData.cliente?.email || undefined,
        id: saleData.cliente?.id,
      },
      items: saleData.items.map((item) => ({
        productoId: item.productId,
        cantidad: item.quantity,
        precioUnitario: item.price,
      })),
      posId: saleData.posId, // Incluir el ID del POS
    };

    // Primero registramos la venta
    const saleResponse = await apiClient.post("/api/pos/ventas", backendData);
    const sale = saleResponse.data;

    // Nota: Para procesar pagos, se requiere una sesión de caja abierta
    // Por ahora solo registramos la venta y retornamos el resultado
    // El pago se puede procesar posteriormente cuando se implemente la gestión de sesiones de caja

    return sale;
  }

  /**
   * Paga una venta en efectivo
   */
  static async paySaleInCash(
    saleId: string,
    paymentData: IPaymentData
  ): Promise<any> {
    try {
      // Verificar sesión de caja activa
      const currentSession = await CashSessionService.getCurrentSession();
      
      if (!currentSession.isOpen || !currentSession.sessionId) {
        throw new Error("No hay una sesión de caja activa. Debe iniciar sesión antes de procesar pagos en efectivo.");
      }

      // Procesar el pago
      const response = await apiClient.post(
        `/api/pos/ventas/${saleId}/pagar/efectivo`,
        {
          ...paymentData,
          sesionCajaId: currentSession.sessionId
        }
      );

      // Registrar movimiento en la sesión de caja
      await CashSessionService.registerMovement(currentSession.sessionId, {
        tipo: 'INGRESO',
        monto: paymentData.amount,
        descripcion: `Venta #${saleId} - Pago en efectivo`,
        cajeroId: paymentData.cajeroId
      });

      return response.data;
    } catch (error) {
      console.error("Error processing cash payment:", error);
      throw error;
    }
  }

  /**
   * Paga una venta con tarjeta
   */
  static async paySaleWithCard(
    saleId: string,
    paymentData: IPaymentData
  ): Promise<any> {
    try {
      // Verificar sesión de caja activa (para registro de ventas)
      const currentSession = await CashSessionService.getCurrentSession();
      
      if (!currentSession.isOpen || !currentSession.sessionId) {
        throw new Error("No hay una sesión de caja activa. Debe iniciar sesión antes de procesar pagos.");
      }

      // Procesar el pago con tarjeta
      const response = await apiClient.post(
        `/api/pos/ventas/${saleId}/pagar/tarjeta`,
        {
          ...paymentData,
          sesionCajaId: currentSession.sessionId
        }
      );

      // Nota: Los pagos con tarjeta no generan movimiento de efectivo en caja
      console.log('Card payment processed for session:', currentSession.sessionId);

      return response.data;
    } catch (error) {
      console.error("Error processing card payment:", error);
      throw error;
    }
  }

  /**
   * Registra un movimiento de caja (ingreso, retiro o ajuste)
   */
  static async registerCashMovement(
    sessionId: string,
    movementData: ICashMovementData
  ): Promise<any> {
    const response = await apiClient.post(
      `/api/caja/turno/${sessionId}/movimiento`,
      movementData
    );
    return response.data;
  }

  /**
   * Consulta los movimientos de una sesión de caja
   */
  static async getCashMovements(
    sessionId: string,
    limit?: number
  ): Promise<ICashMovement[]> {
    const params = limit ? { limite: limit } : {};
    const response = await apiClient.get(
      `/api/caja/turno/${sessionId}/movimientos`,
      { params }
    );
    return response.data.data.movimientos;
  }

  /**
   * Obtiene el resumen de movimientos de una sesión de caja
   */
  static async getCashMovementSummary(
    sessionId: string
  ): Promise<ICashMovementSummary> {
    const response = await apiClient.get(
      `/api/caja/turno/${sessionId}/resumen`
    );
    return response.data.data;
  }

  /**
   * Procesa un pago automático para POS 2 (Pérgola) sin sesión de caja
   */
  static async payBatchSaleAutomatically(
    saleId: string,
    paymentData: IPaymentData
  ): Promise<any> {
    try {
      // Para POS 2, intentar pagar directamente sin validar sesión de caja
      const response = await apiClient.post(
        `/api/pos/ventas/${saleId}/pagar/efectivo`,
        {
          ...paymentData,
          // No incluir sesionCajaId para POS 2
          automaticPayment: true,
          pos2Mode: true
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error processing automatic payment for POS 2:", error);
      throw error;
    }
  }
}

export default POSService;
